import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { IncomingCallCard } from './IncomingCallCard'
import { IncomingCallMatchesModal } from './IncomingCallMatchesModal'
import { ALERT_EXIT_MS, MessageAlertCard } from './MessageAlertCard'
import { QuickCreateContactModal } from 'app/components/contacts/quick-create/QuickCreateContactModal'
import { useIncomingCallAlerts } from 'app/hooks/useIncomingCallAlerts'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import { type MessageAlert, useNewMessageAlerts } from 'app/hooks/useNewMessageAlerts'
import { closeModal, showModal } from 'app/lib/modal'
import type { ContactJson } from 'app/types/entities'
import type { IncomingCall, IncomingCallAlert } from 'app/types/incoming-call'

/**
 * How many cards can share the row. Chosen by breakpoint rather than by measuring
 * available width: measuring is more correct and less predictable, and predictable
 * wins for something that appears unannounced.
 */
const DESKTOP_CAPACITY = 3
const MOBILE_CAPACITY = 1

type RowItem = { kind: `call`; alert: IncomingCallAlert } | { kind: `message`; alert: MessageAlert }

/**
 * Transient cards for inbound texts and ringing calls arriving in any location the
 * current user can access. Anchored bottom-left and growing rightward, away from
 * the nav rail.
 *
 * Dismissing a card is not handling the message: the X only removes the card. The
 * message stays unread, the sidebar badge keeps its count, and the conversation
 * stays bold in the inbox. Clicking through to the thread is what marks it read,
 * through the inbox's own logic.
 *
 * `observer` because the incoming-call source is behind a MobX feature toggle
 * that resolves after mount.
 */
export const MessageAlertStack = observer(function MessageAlertStack(): ReactElement | null {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const routerLocation = useLocation()
  const capacity = isMobile ? MOBILE_CAPACITY : DESKTOP_CAPACITY
  const { alerts, contactNames, locationNames, dismiss } = useNewMessageAlerts(capacity)
  const calls = useIncomingCallAlerts()
  const [exitingIds, setExitingIds] = useState<string[]>([])
  const [navRight, setNavRight] = useState(0)

  // Start the row where the nav rail ends, and only the rail: on /inbox the
  // conversation subnav sits beside it inside the same <aside>, and measuring the
  // aside would push the cards past the thread list and move them page to page.
  // Measured rather than hardcoded to the rail's 280px so it tracks any resize.
  // On mobile the rail is a drawer, not inline, so the offset collapses to 0.
  useEffect(() => {
    if (isMobile) {
      setNavRight(0)
      return
    }
    const rail = document.getElementById(`navs`) ?? document.getElementById(`sidebar-v2`)
    if (!rail) {
      setNavRight(0)
      return
    }
    const measure = () => setNavRight(rail.getBoundingClientRect().right)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(rail)
    window.addEventListener(`resize`, measure)
    return () => {
      observer.disconnect()
      window.removeEventListener(`resize`, measure)
    }
  }, [isMobile])

  // Let the card play its collapse before it leaves the list, so survivors slide
  // across rather than teleporting. `remove` is whichever source owns the card.
  const beginDismiss = useCallback((id: string, remove: (id: string) => void) => {
    setExitingIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
    setTimeout(() => {
      remove(id)
      setExitingIds((prev) => prev.filter((exitingId) => exitingId !== id))
    }, ALERT_EXIT_MS)
  }, [])

  const openThread = useCallback(
    (alert: MessageAlert) => {
      beginDismiss(alert.id, dismiss)
      // react-router, not window.location: a full reload would drop SPA state.
      navigate(`/inbox?locationId=${alert.locationId}&tab=${alert.threadId}`)
    },
    [beginDismiss, dismiss, navigate],
  )

  // Unlike a text, opening a caller does not retire the card: the call is still
  // going and the person answering may need to come back to it.
  const openContact = useCallback(
    (contactId: string) => {
      const target = `/contacts?drawer=${contactId}`
      // The contacts page reads ?drawer= once on mount; changing it in place
      // opens nothing. From anywhere else react-router is fine.
      if (routerLocation.pathname.startsWith(`/contacts`)) window.location.assign(target)
      else navigate(target)
    },
    [navigate, routerLocation.pathname],
  )

  // Opening a call pins its card (no more decay, only the X removes it). Several
  // matches open the potential callers dialog; a single match goes to the drawer;
  // a new number opens contact creation.
  const dialogCallIdRef = useRef<string>(undefined)
  const openCall = useCallback(
    (call: IncomingCall) => {
      const matched = calls.matches[call.id]
      if (!matched) return
      calls.pin(call.id)
      if (!matched.length) {
        const openCreated = (contact: ContactJson) => {
          if (!contact.id) return
          calls.setCallerContact(call.id, { ...contact, id: contact.id })
          openContact(contact.id)
        }
        showModal(
          <QuickCreateContactModal
            defaultCellPhone={call.callerPhone}
            defaultLocationId={call.locationId}
            onCreate={openCreated}
            onExistingContact={openCreated}
          />,
          { hideTitle: true, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
          false,
        )
        return
      }
      if (matched.length === 1) {
        openContact(matched[0]!.id)
        return
      }
      dialogCallIdRef.current = call.id
      showModal(
        <IncomingCallMatchesModal
          call={call}
          contacts={matched}
          locationName={calls.locationNames[call.locationId] ?? locationNames[call.locationId]}
          onPick={(contactId) => {
            closeModal()
            openContact(contactId)
          }}
        />,
        {
          useRadix: true,
          isFullScreen: false,
          hideTitle: true,
          removePadding: true,
          panelClassName: `w-screen max-w-md rounded-lg`,
          onClose: () => {
            dialogCallIdRef.current = undefined
          },
        },
      )
    },
    [calls.matches, calls.locationNames, calls.pin, calls.setCallerContact, locationNames, openContact],
  )

  // The open dialog follows the live cards: a card that decayed, was dismissed,
  // or was cleared by a workspace switch takes its dialog with it.
  useEffect(() => {
    const live = new Set(calls.alerts.map((alert) => alert.call.id))
    if (dialogCallIdRef.current && !live.has(dialogCallIdRef.current)) {
      dialogCallIdRef.current = undefined
      closeModal()
    }
  }, [calls.alerts])

  // Calls first: a ringing phone outranks a text that can wait. Both sources
  // already cap themselves, so this slice only matters when both have cards.
  const row: RowItem[] = [
    ...calls.alerts.map((alert) => ({ kind: `call` as const, alert })),
    ...alerts.map((alert) => ({ kind: `message` as const, alert })),
  ].slice(0, capacity)

  if (!row.length) return null

  return (
    <div
      // Drawers and sheets close on a press outside themselves, and the row floats
      // over the page rather than sitting in it. Dismissing a card is a press on the
      // row, not on the contact drawer the row covers.
      data-floating-layer=""
      style={{
        left: navRight + 16,
        // The row and the scheduling dock are both anchored to the bottom of
        // the viewport, on opposite sides, so they never actually collided
        // vertically. The real collision is horizontal: at the dock's
        // published widths (760px expanded, 320px minimized) three cards run
        // past its left edge on ordinary viewport widths. `maxWidth` walls the
        // row off at the dock's left edge so it can never reach under it. The
        // dock publishes its own width as --scheduling-dock-width; the
        // variable is absent everywhere else, so the fallback leaves the row
        // the full width between the nav and the right edge.
        //
        // Note the cap applies whether or not a dock is open, so it does
        // change the no-dock case on narrower desktops: three 320px cards plus
        // their gaps need 984px, so below roughly 1312px of viewport the row
        // now wraps to a second line where it used to stay on one. That is an
        // improvement rather than a regression. Before the cap the third card
        // simply ran off the right edge of the screen and was never seen.
        maxWidth: `calc(100vw - ${navRight + 16}px - var(--scheduling-dock-width, 0px) - 2rem)`,
      }}
      className={classNames(
        // z-40 sits above page content but below modals and the existing
        // ToastContainer (z-50): a text message should not cover a dialog
        // someone is part-way through.
        `pointer-events-none fixed bottom-4 z-40 flex gap-3`,
        // Anchored bottom-left just past the nav, so the newest card sits at
        // the anchor and older ones push right. `flex-wrap-reverse` on desktop
        // means that when `maxWidth` forces a wrap, the overflow line stacks
        // upward above the anchor line instead of running under the dock or
        // off the right edge of the screen.
        isMobile ? `flex-col items-start` : `flex-row flex-wrap-reverse items-end`,
      )}
    >
      {row.map((item) =>
        item.kind === `call` ? (
          <div key={item.alert.call.id} className="pointer-events-auto">
            <IncomingCallCard
              call={item.alert.call}
              contacts={calls.matches[item.alert.call.id]}
              locationName={
                calls.locationNames[item.alert.call.locationId] ?? locationNames[item.alert.call.locationId]
              }
              shownAt={item.alert.shownAt}
              pinned={item.alert.pinned}
              isExiting={exitingIds.includes(item.alert.call.id)}
              onOpen={calls.matches[item.alert.call.id] ? () => openCall(item.alert.call) : undefined}
              onDismiss={() => beginDismiss(item.alert.call.id, calls.dismiss)}
            />
          </div>
        ) : (
          <div key={item.alert.id} className="pointer-events-auto">
            <MessageAlertCard
              alert={item.alert}
              contactName={item.alert.contactId ? contactNames[item.alert.contactId] : undefined}
              locationName={locationNames[item.alert.locationId]}
              isExiting={exitingIds.includes(item.alert.id)}
              onOpen={() => openThread(item.alert)}
              onDismiss={() => beginDismiss(item.alert.id, dismiss)}
            />
          </div>
        ),
      )}
    </div>
  )
})
